import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import TradePasswordSheet from '@/pages/profile/components/TradePasswordSheet';
import { useToast } from '@/components/base/Toast';

interface VaultItem {
  id: string;
  product: string;
  code: string;
  weight: number;
  status: string;
  statusCode: string;
  location: string;
}

interface Address {
  id: string;
  name: string;
  phone: string;
  region: string;
  detail: string;
  isDefault: boolean;
}

export default function PickupPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [vaultItems, setVaultItems] = useState<VaultItem[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [pickupMode, setPickupMode] = useState<'store' | 'express'>('store');
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [showAddressList, setShowAddressList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTradePwd, setShowTradePwd] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setVaultItems([]);
        setAddresses([]);
        setSelectedAddress(null);
        setLoading(false);
        return;
      }

      const [{ data: assets }, { data: addrData }] = await Promise.all([
        supabase
          .from('gold_assets')
          .select('id, product, code, weight, status, location')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('addresses')
          .select('id, name, phone, region, detail, is_default')
          .eq('user_id', userId)
          .order('is_default', { ascending: false }),
      ]);

      const items = (assets || []).map((row) => ({
        id: row.id,
        product: row.product || '',
        code: row.code || '',
        weight: Number(row.weight) || 0,
        status: row.status || '存储中',
        statusCode:
          row.status === '交割中'
            ? 'delivering'
            : row.status === '已提取' || row.status === '已回购'
              ? 'done'
              : 'stored',
        location: row.location || '',
      }));
      setVaultItems(items);

      const addrs = (addrData || []).map((row) => ({
        id: String(row.id),
        name: row.name || '',
        phone: row.phone || '',
        region: row.region || '',
        detail: row.detail || '',
        isDefault: row.is_default ?? false,
      }));
      setAddresses(addrs);
      const defaultAddr = addrs.find((a) => a.isDefault) || addrs[0] || null;
      setSelectedAddress(defaultAddr);
    } catch {
      setVaultItems([]);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleItem = useCallback((id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const totalWeight = vaultItems
    .filter((item) => selectedItems.has(item.id))
    .reduce((sum, item) => sum + item.weight, 0);

  const handleConfirm = useCallback(() => {
    if (selectedItems.size === 0) return;
    setShowTradePwd(true);
  }, [selectedItems]);

  const handleSubmit = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId || selectedItems.size === 0) return;

    setSubmitting(true);

    const selectedAssets = vaultItems.filter((item) => selectedItems.has(item.id));
    const totalW = selectedAssets.reduce((s, i) => s + i.weight, 0);
    const productName = selectedAssets.map((a) => a.product).join(', ') || '';
    const assetIds = selectedAssets.map((a) => a.id);
    const addressDetail = pickupMode === 'express' && selectedAddress
      ? `${selectedAddress.name} ${selectedAddress.phone} ${selectedAddress.region} ${selectedAddress.detail}`
      : null;

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const createTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const pickId = `PICK-${Date.now()}`;

    const { error: insertErr } = await supabase.from('pickup_records').insert({
      id: pickId,
      user_id: userId,
      asset_ids: assetIds,
      product: productName,
      total_weight: totalW,
      method: pickupMode,
      status: 'pending',
      address_id: pickupMode === 'express' ? selectedAddress?.id || null : null,
      address_detail: addressDetail,
      logistics_no: null,
      fee: 0,
      create_time: createTime,
    });

    if (insertErr) {
      console.error('提货申请写入失败', insertErr);
      setSubmitting(false);
      setShowTradePwd(false);
      showToast('申请提交失败，请重试', 'error');
      return;
    }

    // 更新 gold_assets 状态为"申请提货中"
    for (const assetId of assetIds) {
      await supabase.from('gold_assets').update({ status: '申请提货中' }).eq('id', assetId);
    }

    setSubmitting(false);
    setShowTradePwd(false);
    setSubmitted(true);
    setTimeout(() => navigate('/profile'), 2000);
  }, [navigate, selectedItems, vaultItems, pickupMode, selectedAddress]);

  const storedItems = vaultItems.filter(
    (item) => item.statusCode === 'stored' && item.status !== '申请提货中',
  );

  // 历史资产（已提取/已回购），用于展示记录入口数量
  const historyCount = vaultItems.filter((item) => item.statusCode === 'done').length;

  if (submitted) {
    return (
      <div className="min-h-screen bg-brown-50 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-brown-900 text-lg font-bold mb-2">提货申请已提交</h2>
        <p className="text-brown-500 text-sm text-center">
          共提取 {totalWeight}g 黄金，{pickupMode === 'store' ? '请前往指定网点自提' : '将安排快递配送'}
        </p>
        <p className="text-brown-400 text-xs mt-6">正在返回我的页面...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50 pb-48">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">申请提货</span>
        <button
          onClick={() => navigate('/profile/pickup/records')}
          className="text-gold-400 text-xs font-medium cursor-pointer whitespace-nowrap"
        >
          提货记录{historyCount > 0 ? `(${historyCount})` : ''}
        </button>
      </div>

      {/* Vault Items */}
      <div className="mx-4 mt-4">
        <p className="text-brown-700 text-sm font-bold mb-3">选择提货黄金</p>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {storedItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`w-full bg-white rounded-xl p-4 shadow-sm flex items-start gap-3 text-left cursor-pointer transition-all ${
                    selectedItems.has(item.id) ? 'ring-1 ring-gold-400' : ''
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    selectedItems.has(item.id) ? 'border-gold-500 bg-gold-500' : 'border-gray-300'
                  }`}>
                    {selectedItems.has(item.id) && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-brown-800 text-sm font-medium">{item.product}</p>
                      <span className="bg-green-50 text-green-600 text-[10px] px-2 py-0.5 rounded-full">{item.status}</span>
                    </div>
                    <p className="text-brown-400 text-xs mt-1">编码: {item.code}</p>
                    <p className="text-brown-400 text-xs">重量: {item.weight}g · 存放: {item.location}</p>
                  </div>
                </button>
              ))}
            </div>
            {storedItems.length === 0 && (
              <div className="text-center py-10">
                <i className="ri-inbox-line text-brown-300 text-4xl"></i>
                <p className="text-brown-400 text-sm mt-3">暂无可提货黄金</p>
                {historyCount > 0 && (
                  <button
                    onClick={() => navigate('/profile/pickup/records')}
                    className="mt-3 text-gold-600 text-xs cursor-pointer whitespace-nowrap"
                  >
                    查看提货记录 <i className="ri-arrow-right-s-line"></i>
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pickup Mode */}
      <div className="mx-4 mt-4 bg-white rounded-xl p-4 shadow-sm">
        <p className="text-brown-700 text-sm font-bold mb-3">提货方式</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPickupMode('store')}
            className={`rounded-xl border p-3 text-center cursor-pointer transition-all ${
              pickupMode === 'store'
                ? 'border-gold-500 bg-gold-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <i className={`ri-store-2-line text-xl ${pickupMode === 'store' ? 'text-gold-600' : 'text-gray-400'}`}></i>
            <p className={`text-sm font-medium mt-1 ${pickupMode === 'store' ? 'text-gold-700' : 'text-brown-800'}`}>到店自提</p>
            <p className="text-brown-400 text-[10px] mt-0.5">前往指定网点</p>
          </button>
          <button
            onClick={() => setPickupMode('express')}
            className={`rounded-xl border p-3 text-center cursor-pointer transition-all ${
              pickupMode === 'express'
                ? 'border-gold-500 bg-gold-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <i className={`ri-truck-line text-xl ${pickupMode === 'express' ? 'text-gold-600' : 'text-gray-400'}`}></i>
            <p className={`text-sm font-medium mt-1 ${pickupMode === 'express' ? 'text-gold-700' : 'text-brown-800'}`}>快递配送</p>
            <p className="text-brown-400 text-[10px] mt-0.5">顺丰全额保价</p>
          </button>
        </div>
      </div>

      {/* Address for Express */}
      {pickupMode === 'express' && selectedAddress && (
        <div className="mx-4 mt-4 bg-white rounded-xl p-4 shadow-sm">
          <p className="text-brown-700 text-sm font-bold mb-3">收货地址</p>
          <button
            onClick={() => setShowAddressList(true)}
            className="w-full flex items-center justify-between text-left cursor-pointer"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="text-brown-800 text-sm font-medium">{selectedAddress.name}</span>
                <span className="text-brown-500 text-xs">{selectedAddress.phone}</span>
              </div>
              <p className="text-brown-400 text-xs mt-1">{selectedAddress.region} {selectedAddress.detail}</p>
            </div>
            <i className="ri-arrow-right-s-line text-brown-300"></i>
          </button>
        </div>
      )}

      {pickupMode === 'express' && !selectedAddress && !loading && (
        <div className="mx-4 mt-4 bg-amber-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-information-line text-amber-500"></i>
            <span className="text-amber-700 text-sm font-medium">暂无收货地址</span>
          </div>
          <p className="text-amber-600 text-xs leading-relaxed">
            请先前往「我的 → 收货地址」添加地址后再申请快递配送。
          </p>
        </div>
      )}

      {/* Store Pickup Info */}
      {pickupMode === 'store' && (
        <div className="mx-4 mt-4 bg-amber-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-information-line text-amber-500"></i>
            <span className="text-amber-700 text-sm font-medium">到店提货须知</span>
          </div>
          <p className="text-amber-600 text-xs leading-relaxed">
            请携带本人有效身份证件，前往存放金库网点办理提货手续。提货前请提前联系网点预约时间。
          </p>
        </div>
      )}

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pt-3 pb-5 z-40">
        <div className="flex items-center justify-between mb-3">
          <span className="text-brown-500 text-sm">提货重量</span>
          <span className="text-brown-900 text-lg font-bold">{totalWeight}g</span>
        </div>
        <button
          onClick={handleConfirm}
          disabled={selectedItems.size === 0}
          className={`w-full py-3 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all ${
            selectedItems.size > 0
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          确认提货
        </button>
      </div>

      {/* Address List Modal */}
      {showAddressList && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white animate-fade-up">
          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
            <button onClick={() => setShowAddressList(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-close-line text-brown-900 text-xl"></i>
            </button>
            <span className="text-brown-900 text-sm font-bold">选择收货地址</span>
            <div className="w-8" />
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {addresses.map((addr) => (
              <button
                key={addr.id}
                onClick={() => { setSelectedAddress(addr); setShowAddressList(false); }}
                className={`w-full p-4 rounded-xl mb-2 text-left cursor-pointer ${
                  selectedAddress?.id === addr.id ? 'bg-gold-50 border border-gold-200' : 'bg-white border border-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-brown-800 text-sm font-medium">{addr.name}</span>
                    <span className="text-brown-500 text-xs">{addr.phone}</span>
                  </div>
                  {addr.isDefault && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold-100 text-gold-700">默认</span>
                  )}
                </div>
                <p className="text-brown-400 text-xs mt-1">{addr.region} {addr.detail}</p>
              </button>
            ))}
            {addresses.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-map-pin-line text-brown-300 text-4xl"></i>
                <p className="text-brown-400 text-sm mt-3">暂无收货地址</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trade Password Sheet */}
      <TradePasswordSheet
        open={showTradePwd}
        onClose={() => setShowTradePwd(false)}
        onSuccess={handleSubmit}
        title="提货验证"
      />
    </div>
  );
}